// agents/src/services/blockedContactService.js

import { getApiConfig } from '../config/markets.js';

const API_URL = getApiConfig().apiUrl;

/**
 * Obtener lista de contactos bloqueados
 * @param {string} [market] - Filtrar por mercado (opcional)
 * @returns {Promise<Object>} - { blockedContacts, total }
 */
export async function getBlockedContacts(market = null) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const params = new URLSearchParams();
  if (market) {
    params.append('market', market);
  }

  const res = await fetch(`${API_URL}/blocked-contacts?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al obtener contactos bloqueados';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Verificar si un contacto está bloqueado
 * @param {string} whatsappId - Número de WhatsApp
 * @returns {Promise<Object>} - { isBlocked, blockedContact }
 */
export async function checkIfBlocked(whatsappId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/blocked-contacts/check/${whatsappId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al verificar bloqueo';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Bloquear un contacto
 * @param {string} whatsappId - Número de WhatsApp
 * @param {string} [reason] - Razón del bloqueo
 * @param {string} [conversationId] - ID de conversación (para obtener mercado)
 * @returns {Promise<Object>} - Datos del contacto bloqueado
 */
export async function blockContact(whatsappId, reason = '', conversationId = null) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const payload = { whatsappId };
  if (reason) payload.reason = reason;
  if (conversationId) payload.conversationId = conversationId;

  const res = await fetch(`${API_URL}/blocked-contacts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    let errMsg = 'Error al bloquear contacto';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Desbloquear un contacto
 * @param {string} whatsappId - Número de WhatsApp
 * @returns {Promise<Object>} - Mensaje de confirmación
 */
export async function unblockContact(whatsappId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/blocked-contacts/${whatsappId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    let errMsg = 'Error al desbloquear contacto';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Actualizar razón de bloqueo
 * @param {string} whatsappId - Número de WhatsApp
 * @param {string} reason - Nueva razón
 * @returns {Promise<Object>} - Datos actualizados
 */
export async function updateBlockReason(whatsappId, reason) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/blocked-contacts/${whatsappId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ reason })
  });

  if (!res.ok) {
    let errMsg = 'Error al actualizar razón';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}
